/**
 * Workflow Builder status convention
 *
 * There is no backend field for a workflow's draft/published status today
 * (see ADR-0021), so the Workflow Builder carries it client-side inside a
 * reserved key in the workflow's `input` schema. ADR-0022 records the staged
 * path to a real backend field; this file is deliberately the single swap
 * point for that migration. This is the only schema-legal home that round-trips through both
 * SDKs: strict Zod schemas strip unknown top-level keys, and
 * `agents.*.parameters` is regenerated (and so discarded) whenever the
 * build-agent regenerates the workflow.
 *
 * Absence of the key means "published", so every workflow that predates
 * this convention is treated correctly without migration. Publishing
 * removes the key entirely.
 */

import type { Workflow } from "@/stores/WorkflowStore";

/** Reserved key inside a workflow's `input` schema holding the Builder status. */
export const WORKFLOW_STATUS_INPUT_KEY = "pathway_status";

/** Client-side draft/published status for a workflow, per the Workflow Builder. */
export type WorkflowBuilderStatus = "draft" | "published";

/** Shape of a single entry inside a workflow's `input` schema. */
export interface InputProperty {
  type: string;
  description: string;
  value?: string;
}

const STATUS_DESCRIPTION = "Managed by Pathway. Do not edit.";

/**
 * Reads the client-side draft/published status from a workflow's input
 * schema. Absence of the key (or of the input object entirely) means
 * "published".
 */
export function getWorkflowStatus(workflow: Workflow | null | undefined): WorkflowBuilderStatus {
  const statusEntry = workflow?.input?.[WORKFLOW_STATUS_INPUT_KEY] as InputProperty | undefined;

  return statusEntry?.value === "draft" ? "draft" : "published";
}

/**
 * Returns a new workflow with the draft/published status applied. Pure —
 * never mutates the input workflow or its nested `input` object.
 *
 * Setting "draft" writes a valid InputProperty entry under
 * WORKFLOW_STATUS_INPUT_KEY. Setting "published" removes the key entirely,
 * leaving the rest of the input schema untouched (an input object that
 * becomes empty as a result is left in place rather than deleted, to keep
 * the operation simple and unambiguous about what it owns).
 */
export function withWorkflowStatus(workflow: Workflow, status: WorkflowBuilderStatus): Workflow {
  if (status === "draft") {
    return {
      ...workflow,
      input: {
        ...workflow.input,
        [WORKFLOW_STATUS_INPUT_KEY]: {
          type: "string",
          description: STATUS_DESCRIPTION,
          value: "draft",
        },
      },
    };
  }

  // status === "published": remove the reserved key entirely.
  if (!workflow.input || !(WORKFLOW_STATUS_INPUT_KEY in workflow.input)) {
    return workflow;
  }

  const updatedInput = { ...workflow.input };

  delete updatedInput[WORKFLOW_STATUS_INPUT_KEY];

  return { ...workflow, input: updatedInput };
}

/**
 * True for input keys reserved for internal Pathway bookkeeping rather than
 * a user-authored workflow parameter. Leak sites (the Input node, the
 * InputPropertiesPanel) filter these out so the flag never renders on the
 * canvas or becomes user-editable.
 */
export function isReservedInputKey(key: string): boolean {
  return key === WORKFLOW_STATUS_INPUT_KEY;
}

/**
 * Returns a workflow's `input` schema with every reserved bookkeeping key
 * removed, or the original object when there is nothing to strip.
 *
 * This is what keeps the convention invisible. `pathway_status` and its
 * "Managed by Pathway" description are an implementation detail of this
 * frontend — neither admin nor agent-platform knows the key exists — so any
 * surface that shows a definition to a user, or lets one be copied out of
 * the app, must serialise the stripped shape rather than the stored one.
 */
export function stripReservedInputKeys(
  input: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null | undefined {
  if (!input) return input;

  const reserved = Object.keys(input).filter(isReservedInputKey);

  if (reserved.length === 0) return input;

  const stripped = { ...input };

  for (const key of reserved) delete stripped[key];

  return stripped;
}

/**
 * Carries the draft/published status forward from `previous` onto `next`
 * when `next` omits it, mirroring how `id` and `scheduled` survive a
 * round-trip through YAML or the canvas (see `setWorkflowFromYaml` and
 * `nodesToWorkflow`).
 *
 * Every producer of a fresh definition omits the key: the build agent is
 * never told the convention, the Input node filters it out before the canvas
 * ever sees it, and the code editor is now shown the stripped YAML. Without
 * this, each of those paths silently republished a draft.
 *
 * A definition that carries the key already is returned untouched, so a
 * producer that deliberately sets a status is never overridden by the
 * previous one. Note that this branch does NOT protect publishing: a
 * published definition has no key (absence means published), so if this
 * function ran on one with a draft `previous`, the draft WOULD be carried
 * back onto it. Publishing works because `useBuilderPublish` stamps the
 * status (`withWorkflowStatus`) after every preserve site in its path and
 * saves without coming back through here — reorder that path and the stamp
 * is silently undone.
 */
export function preserveWorkflowStatus<T extends { input?: Record<string, unknown> | null }>(
  next: T,
  previous: { input?: Record<string, unknown> | null } | null | undefined,
): T {
  const nextInput = next.input;

  if (nextInput && WORKFLOW_STATUS_INPUT_KEY in nextInput) return next;

  const carried = previous?.input?.[WORKFLOW_STATUS_INPUT_KEY];

  if (carried === undefined) return next;

  return {
    ...next,
    input: { ...(nextInput ?? {}), [WORKFLOW_STATUS_INPUT_KEY]: carried },
  };
}
